import { useEffect, useState } from 'react';
import {
  APP_STORE_ID,
  IS_APP_PUBLISHED,
  iosStoreUrl,
  androidStoreUrl,
} from '../config/storeConfig';

const DISMISS_KEY = 'marginpal.mobileAppBanner.dismissed';

function detectPlatform() {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  return null;
}

// Apple's native Smart App Banner only renders in Safari on iOS; other iOS
// browsers (Chrome, Firefox, Edge) ignore the meta tag, so we still want the
// custom banner there.
function isIosSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iosLike = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return iosLike && isSafari;
}

export default function MobileAppBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  // Apple Smart App Banner meta tag — iOS Safari renders a native banner.
  useEffect(() => {
    if (!IS_APP_PUBLISHED || !APP_STORE_ID) return;
    if (document.querySelector('meta[name="apple-itunes-app"]')) return;
    const meta = document.createElement('meta');
    meta.name = 'apple-itunes-app';
    meta.content = `app-id=${APP_STORE_ID}`;
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);

  if (!IS_APP_PUBLISHED) return null;
  if (dismissed) return null;

  const platform = detectPlatform();
  if (!platform) return null;
  if (isIosSafari()) return null;

  const url = platform === 'ios' ? iosStoreUrl() : androidStoreUrl();
  if (!url) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div style={styles.banner} role="region" aria-label="Get the MarginPal mobile app">
      <div style={styles.content}>
        <div style={styles.title}>Get the MarginPal app</div>
        <div style={styles.subtitle}>A better experience on your phone.</div>
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-sm btn-primary"
        style={styles.cta}
      >
        {platform === 'ios' ? 'App Store' : 'Google Play'}
      </a>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        style={styles.close}
      >
        ×
      </button>
    </div>
  );
}

const styles = {
  banner: {
    position: 'fixed',
    top: 0, left: 0, right: 0,
    zIndex: 1080,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    background: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
  },
  content: { flex: 1, minWidth: 0 },
  title: { fontSize: 14, fontWeight: 600, color: '#111827', lineHeight: 1.2 },
  subtitle: { fontSize: 12, color: '#6b7280', lineHeight: 1.2, marginTop: 2 },
  cta: { whiteSpace: 'nowrap' },
  close: {
    background: 'transparent',
    border: 'none',
    fontSize: 22,
    lineHeight: 1,
    color: '#6b7280',
    padding: '0 4px',
    cursor: 'pointer',
  },
};
